import { VerificaDocumentoAI } from '@framework/api-client';
import {
  FormBuilderMetaFlags,
  parseAdvancedRichTextToString,
} from '@framework/engine';
import { t } from '@framework/i18n';
import { yup } from '@framework/validation';
import { IUploadWidget } from './IUploadWidget';
import { publicDataUploadWidgetName } from './UploadWidget.type';

export function uploadWidgetFilenameField(code: string) {
  return `${code}@filename`;
}

export function uploadWidgetVerifyDocumentAiField(code: string) {
  return `${code}@verifyDocumentAi`;
}

export function createUplaodWidgetFormSchema(input: { widget: IUploadWidget }) {
  const { widget } = input;

  const publicUpload = widget.directives?.omissis?.enabled ?? false;
  const widgetName = widget.code;
  const filenameField = uploadWidgetFilenameField(widgetName);
  const verifyDocumentAIField = uploadWidgetVerifyDocumentAiField(widgetName);
  // Se gli omissis sono attivi, aggiungo un campo per salvare i dati del file "pubblico"
  const publicWidgetName = publicDataUploadWidgetName(widgetName);
  const publicFilenameField = uploadWidgetFilenameField(publicWidgetName);
  const publicVerifyDocumentAIField =
    uploadWidgetVerifyDocumentAiField(publicWidgetName);

  const verifyDocumentAISchema = yup.object<VerificaDocumentoAI>({
    flag_identification: yup.boolean().notRequired(),
    score: yup.number().notRequired(),
    document_type: yup.string().notRequired(),
  });

  let publicLabelText: string | null = null;
  if (widget.publicUploadLabel) {
    const publicLabel = widget.publicUploadLabel ?? null;
    publicLabelText = parseAdvancedRichTextToString(publicLabel);
  }

  const publicUploadSchema = {
    [publicWidgetName]: yup
      .string()
      .notRequired()
      .typeError(
        t(
          'upload-widget.file-must-be-uploaded',
          'Il file ${label} deve essere caricato',
        ),
      )
      .label(
        publicLabelText ?? t('upload-widget.public-doc', 'Documento pubblico'),
      )
      // Questo campo è sempre opzionale
      .formBuilderFlags(FormBuilderMetaFlags.IgnoreRequired),
    [publicFilenameField]: yup
      .string()
      .notRequired()
      .formBuilderFlags(
        FormBuilderMetaFlags.SkipDefaultValue,
        FormBuilderMetaFlags.SkipValidationRules,
      )
      .when([publicWidgetName ?? '<Not-Defined>'], ([code], schema: any) => {
        if (!code) {
          // Se non c'è un file selezionato, dobbiamo rimuovere il valore
          // del nome del file.
          return schema.transform(() => null);
        }
        return schema;
      }),
    [publicVerifyDocumentAIField]: verifyDocumentAISchema
      .clone()
      .formBuilderFlags(
        FormBuilderMetaFlags.SkipDefaultValue,
        FormBuilderMetaFlags.SkipValidationRules,
      )
      .notRequired(),
  };

  return {
    [widgetName]: yup
      .string()
      .notRequired()
      .typeError(
        t(
          'upload-widget.file-must-be-uploaded',
          'Il file ${label} deve essere caricato',
        ),
      ),
    [filenameField]: yup
      .string()
      .notRequired()
      .formBuilderFlags(
        FormBuilderMetaFlags.SkipDefaultValue,
        FormBuilderMetaFlags.SkipValidationRules,
      )
      .when([widgetName ?? '<Not-Defined>'], ([code], schema: any) => {
        if (!code) {
          // Se non c'è un file selezionato, dobbiamo rimuovere il valore
          // del nome del file.
          return schema.transform(() => null);
        }
        return schema;
      }),
    [verifyDocumentAIField]: verifyDocumentAISchema
      .clone()
      .formBuilderFlags(
        FormBuilderMetaFlags.SkipDefaultValue,
        FormBuilderMetaFlags.SkipValidationRules,
      )
      .notRequired(),
    ...(publicUpload ? publicUploadSchema : {}),
  };
}
